import { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"

import { Page } from "src/components/page"
import { Heading, Subheading } from "src/components/heading"
import { Combobox, ComboboxOption, ComboboxLabel, ComboboxDescription } from "src/components/combobox"
import { Button } from "src/components/button"
import { Card } from "src/components/card"
import { BorderlessTextarea } from "src/components/textarea"
import { Icon } from "src/components/icon"
import { Dropdown, DropdownButton, DropdownMenu, DropdownItem } from "src/components/dropdown"

import { setRefresh } from "src/store/chat.jsx"

import { apiClient } from "src/utils"

export default function Home() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [message, setMessage] = useState("")
  const [company, setCompany] = useState(null)
  const [industry, setIndustry] = useState(null)
  const [sector, setSector] = useState(null)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const user = useSelector((state) => state.auth.user)
  const companies = useSelector((state) => state.company.companyList)
  const sectors = useSelector((state) => state.company.sectorList)
  const industries = useSelector((state) => state.company.industryList)

  const handleSubmit = () => {
    if (message.trim() === "") return

    setLoading(true)
    setError(null)

    apiClient
      .post("/chat/new", {
        message: message,
        company_uuid: company?.uuid,
        sector: sector,
        industry: industry,
      })
      .then((response) => {
        navigate(`/chat/${response.uuid}`)
        setMessage("")
        setCompany(null)
        setSector(null)
        setIndustry(null)
        dispatch(setRefresh(true))
      })
      .catch(() => {
        setError(true)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <Page className="flex flex-col gap-4 min-h-[calc(100svh-10rem)] justify-center items-center">
      <Subheading>Hi {user?.first_name}</Subheading>
      <Heading className="text-center font-serif !text-xl sm:!text-2xl mb-4">
        Your next insight is in the filings
      </Heading>
      <Card className="w-full sm:w-[80%] !p-4 border">
        <BorderlessTextarea
          className="min-h-12 w-full !text-base sm:!text-sm"
          placeholder="Ask a question..."
          value={message}
          onChange={(message) => setMessage(message)}
          disabled={loading}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && message.trim() !== "" && !loading) {
              e.preventDefault()
              handleSubmit()
            }
          }}
          rows={3}
        />

        <div className="flex flex-row gap-2 items-center justify-between w-full">
          <div className="hidden sm:flex flex-row gap-2 items-center">
            <Combobox
              name="company"
              options={companies}
              displayValue={(company) => company?.name}
              value={company}
              filter={(company, query) =>
                company?.name?.toLowerCase().includes(query.toLowerCase()) ||
                company?.ticker?.toLowerCase().includes(query.toLowerCase())
              }
              onChange={(company) => setCompany(company)}
              placeholder="Company"
              aria-label="Company"
              className="!w-fit !text-base sm:!text-xs"
            >
              {(company) => (
                <ComboboxOption value={company}>
                  <img src={company?.icon} className="w-4 h-4 rounded-full" alt={company?.name} />
                  <ComboboxLabel>{company?.name}</ComboboxLabel>
                  <ComboboxDescription>{company?.ticker}</ComboboxDescription>
                </ComboboxOption>
              )}
            </Combobox>
            <Dropdown>
              <DropdownButton plain className="!text-base sm:!text-xs">
                {industry ? industry : "Industry"}
                {/* <Icon icon="ChevronDown" className="w-4 h-4 !text-black dark:!text-white" /> */}
              </DropdownButton>
              <DropdownMenu>
                {industries.map((industry) => (
                  <DropdownItem key={industry} onClick={() => setIndustry(industry)}>
                    {industry}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
            <Dropdown>
              <DropdownButton plain className="!text-base sm:!text-xs">
                {sector ? sector : "Sector"}
                {/* <Icon icon="ChevronDown" className="w-4 h-4 !text-black dark:!text-white" /> */}
              </DropdownButton>
              <DropdownMenu>
                {sectors.map((sector) => (
                  <DropdownItem key={sector} onClick={() => setSector(sector)}>
                    {sector}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>
          <Button
            className="ml-auto sm:ml-0 whitespace-nowrap !text-xs"
            color="emerald"
            onClick={handleSubmit}
            disabled={loading || message.trim() === ""}
          >
            {/* <Icon icon="ArrowTurnDownLeft" className="w-4 h-4 !text-white" /> */}
            Go
          </Button>
        </div>
      </Card>
      {error && (
        <div className="flex flex-row items-center justify-center">
          <Icon icon="ExclamationTriangle" className="w-4 h-4 !text-red-500" />
          <Text>Error creating chat, try again.</Text>
        </div>
      )}
      <div className="flex flex-col sm:hidden gap-2 items-center w-full px-4">
        <Combobox
          name="company"
          options={companies}
          displayValue={(company) => company?.name}
          value={company}
          filter={(company, query) =>
            company?.name?.toLowerCase().includes(query.toLowerCase()) ||
            company?.ticker?.toLowerCase().includes(query.toLowerCase())
          }
          onChange={(company) => setCompany(company)}
          placeholder="Company"
          aria-label="Company"
          className="!w-full !text-base sm:!text-xs"
        >
          {(company) => (
            <ComboboxOption value={company}>
              <img src={company?.icon} className="w-4 h-4 rounded-full" alt={company?.name} />
              <ComboboxLabel>{company?.name}</ComboboxLabel>
              <ComboboxDescription>{company?.ticker}</ComboboxDescription>
            </ComboboxOption>
          )}
        </Combobox>
        <Dropdown>
          <DropdownButton outline className="!text-base sm:!text-xs !w-full">
            {industry ? industry : "Industry"}
            <Icon icon="ChevronDown" className="w-4 h-4 !text-black dark:!text-white" />
          </DropdownButton>
          <DropdownMenu>
            {industries.map((industry) => (
              <DropdownItem key={industry} onClick={() => setIndustry(industry)}>
                {industry}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
        <Dropdown>
          <DropdownButton outline className="!text-base sm:!text-xs !w-full">
            {sector ? sector : "Sector"}
            <Icon icon="ChevronDown" className="w-4 h-4 !text-black dark:!text-white" />
          </DropdownButton>
          <DropdownMenu>
            {sectors.map((sector) => (
              <DropdownItem key={sector} onClick={() => setSector(sector)}>
                {sector}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      </div>
    </Page>
  )
}
